import {createSlice} from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import without from 'lodash/without';
import dayjs from 'utils/dayjs';
import {displayDateFormat} from 'hooks/config';
import {
    createFacilityBlackouts,
    deleteFacilityBlackout,
    getAllBlackouts,
} from './blackouts-actions';
import {blackoutsAdapter} from './blackouts-adapters';

const now = dayjs();

/**
 *
 * @typedef {object} TBlackoutsSliceInitialState
 * @property {object} filters
 * @property {string} filters.dateFrom - The start date for the report in the format MM/DD/YYYY.  Defaults to the current date
 * @property {string} filters.dateTo - The end date for the report in the format MM/DD/YYYY.  Defaults to 30 days from the current date
 * @property {('all'|'active'|'upcoming'|'disabled'|'expired')} [filters.blackoutStatus="all"] - The type of reservation to include in the report.  Defaults to 'all'
 * @property {number} filters.numFiltersApplied - The number of filters that have been applied.  Defaults to 0
 * @property {boolean} isBlackoutsModalOpen - Whether or not the blackouts modal is open
 */
export const initialState = {
    filters: {
        dateFrom: now.format(displayDateFormat),
        dateTo: now.add(6, 'month').format(displayDateFormat),
        blackoutStatus: 'all',
        numFiltersApplied: 0,
    },
    isBlackoutsModalOpen: false,
    isLoading: false,
    data: blackoutsAdapter.getInitialState({ids: [], entities: {}, meta: {}}),
};

const blackoutsSlice = createSlice({
    name: 'blackouts',
    initialState,
    reducers: {
        /**
         * Updates the filters for the blackouts
         * @memberof Blackouts.Actions
         * @param {TBlackoutsSliceInitialState} [state] - The current state
         * @param {object} payload - The new filters
         * @param {string} payload.dateFrom - The start date for the report in the format MM/DD/YYYY. Defaults to the current date
         * @param {string} payload.dateTo - The end date for the report in the format MM/DD/YYYY. Defaults to the current date
         * @param {("all"|"active"|"upcoming"|"disabled"|"expired")} payload.blackoutStatus - The type of reservation to include in the report.
         *
         * @returns {dateFrom: string, dateTo: string, blackoutStatus: string, numFiltersApplied: number}
         */
        updateFilters: {
            reducer: (state, {payload}) => {
                state.filters = payload;
            },
            prepare: (payload) => {
                const initialFilters = omit(
                    initialState.filters,
                    'numFiltersApplied'
                );

                const appliedFilters = without(
                    Object.values(payload),
                    ...Object.values(initialFilters)
                );

                return {
                    payload: {
                        ...payload,
                        numFiltersApplied: appliedFilters.length,
                    },
                };
            },
        },

        /**
         * Toggles the blackouts modal
         * @memberof Blackouts.Actions
         * @param {TBlackoutsSliceInitialState} state
         * @returns {boolean}
         */ toggleBlackoutsModal: (state) => {
            state.isBlackoutsModalOpen = !state.isBlackoutsModalOpen;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllBlackouts.fulfilled, (state, {payload}) => {
                const {data, meta} = payload;

                blackoutsAdapter.setAll(state.data, data);

                state.data.meta = meta;
                state.isLoading = false;
            })
            .addCase(getAllBlackouts.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getAllBlackouts.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.isLoading = false;
            })
            .addCase(deleteFacilityBlackout.fulfilled, (state, {payload}) => {
                blackoutsAdapter.removeOne(state.data, payload);
                state.isLoading = false;
            })
            .addCase(deleteFacilityBlackout.pending, (state) => {
                state.isLoading = true;
            })

            .addCase(createFacilityBlackouts.fulfilled, (state, {payload}) => {
                const {data} = payload;

                blackoutsAdapter.addMany(state.data, data);
            });
    },
});

export const {updateFilters, toggleBlackoutsModal} = blackoutsSlice.actions;
export default blackoutsSlice.reducer;
